'use client';

/**
 * Parses the MFour iOOH Brand Lift crosstab Excel file into a structured
 * format that build-deck-from-iooh.ts consumes to generate a populated Slide[].
 *
 * Supports two export formats from the MFour crosstab tool:
 *
 *  FORMAT A — "Table" format (legacy):
 *    Section headers: "Table 5. OOHABA. Question text"
 *    Base rows:       "Base  228  228  81  0"
 *    Stat sig rows:   separate row below data containing only capital letters
 *
 *  FORMAT B — "Flat" format (new studio export):
 *    Section headers: "OOHABA — Question text"
 *    Bases:           embedded in column header row as "Unexposed\nn=228"
 *    Stat sig:        embedded in data cells as "4% C" (letter follows the %)
 *    Column layout:   Group | Other(complement) | Group | Other | ...
 */

import { columnLetter } from '@/lib/crosstab/column-letter';
import { displayOfNumber } from '@/lib/crosstab/xlsx-number-format';
import ExcelJS from 'exceljs';
import { dataSheetIndex, isExtraTableTitle, isFootnoteText } from '@/lib/crosstab/summary-tables';

// A column header's label and base, read apart (M4S-5626). Defined in its own file so the slide
// exporter can use it without loading ExcelJS; re-exported here for callers that already import the parser.
import { splitColumnHeader } from './iooh-column-header';
export { splitColumnHeader };

// ── Known section codes (MFour_iOOH_Report_Guide_V3.docx) ────────────────────

export const KNOWN_IOOH_CODES = new Set([
    'OOHUBA',
    'OOHABA',
    'OOHUAA',
    'OOHAAA',
    'OOHAR',
    'OOHMM',
    'OOHLIKE',
    'OOHABO',
    'OOHBO',
    'OOHCON',
    'OOHPUR',
    'OOHNPS',
]);

export const KNOWN_DEMO_CODES = new Set([
    'GENDER',
    'AGE',
    'ETHN',
    // The recoded ethnicity slide 20's five rows are (BT-2224).
    'ETHNCV',
    'INCOME',
    'EDUCATION',
    'EMPLOY',
    'RELATIONSHIPSTATUS',
    'OOHPEXP',
]);

export const ALL_KNOWN_CODES = new Set([...KNOWN_IOOH_CODES, ...KNOWN_DEMO_CODES]);

/**
 * Non-standard codes that the crosstab tool may emit for specific question
 * types. We normalise them to the canonical code before storing so all
 * downstream consumers (export-iooh-pptx, build-deck-from-iooh) can look
 * up a single known key.
 */
const CODE_ALIASES: Record<string, string> = {
    OOHRECALL: 'OOHAR',
};

/** The four core columns of an iOOH crosstab, named the way `IoohDataRow` and `IoohSection.bases` are. */
export type IoohGroup = 'unexposed' | 'exposed' | 'adRecognizer' | 'noAdRecall';

/**
 * How the on-screen adapter (`build-iooh-from-computed.ts`) recognises the two recall columns, by
 * their whole label (lowercase, outer spaces aside). Two producers name them differently:
 *
 *  - the four-variable base (`cv_bl_*`) labels them "Ad Recognizer" and "No Ad Recall";
 *  - the combined `CELLADRECALLCV` question labels its third and fourth options "Ad Recall" and
 *    "Non-Ad Recall", so those two columns are the recognizers and the no-recall respondents.
 *
 * "Ad Recall" is the one label that means something else elsewhere (a lone `ADRECALLCV` banner
 * column is every recaller, not the exposed ones who recalled the ad), so the adapter takes it only
 * on a column of the combined question. The Excel parser keeps its own keyword search below,
 * because its headers carry "n=" text.
 */
export const AD_RECOGNIZER_COLUMN_LABELS = ['ad recognizer', 'ad recognizers'];
export const NO_AD_RECALL_COLUMN_LABELS = ['no ad recall', 'non-ad recall', 'non ad recall'];
export const COMBINED_AD_RECALL_LABEL = 'ad recall';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface IoohDataRow {
    label: string;
    /** Category Net / Total rows stay fixed at top — not sorted. */
    isNet: boolean;
    unexposed: number | null;
    exposed: number | null;
    adRecognizer: number | null;
    noAdRecall: number | null;
    /** Values from demographic / banner columns beyond the 4 core groups. */
    extraCols: (number | null)[];
    /**
     * An open end's level in its code frame, when the crosstab says (BT-2121): 0 = a top-level net, 1 = a
     * subnet or a code under a net, 2 = a sub-subnet or a code under a subnet. A Studio crosstab carries
     * it; an Excel upload does not, and its level is read from the labels (`iooh-main-message.ts`).
     */
    depth?: number;
    sig: {
        /** Exposed sig higher than Unexposed (green highlight). */
        exposedSigHigher: boolean;
        /** Unexposed sig higher than Exposed (red highlight). */
        unexposedSigHigher: boolean;
    };
}

export interface IoohSection {
    code: string;
    /**
     * The question as the survey asks it, piped tokens ("{BrandCV}") and all (M4S-5673, BT-2064): the
     * footnotes print it beside the QID. Optional, because a crosstab stored in the browser before it
     * existed has none, and its footnotes keep the bare QID.
     */
    title?: string;
    /** The QID the survey gave the question, when an alias stands in for `code` ("QCUSTOM3" for OOHABO). */
    sourceQid?: string;
    rows: IoohDataRow[];
    bases: {
        unexposed: number;
        exposed: number;
        adRecognizer: number;
        noAdRecall: number;
    };
    extraColHeaders: string[];
}

export interface ParsedIoohCrosstab {
    sections: Record<string, IoohSection>;
    /** Codes not in KNOWN_IOOH_CODES or KNOWN_DEMO_CODES → become QCUSTOM appendix slides. */
    customSectionCodes: string[];
    extraColHeaders: string[];
    /**
     * Which of the four core columns the source really had. A column that is missing and a column
     * with no respondents both leave a base of 0; this tells them apart. Optional so a crosstab
     * stored before it existed (`iooh-crosstab-<conv>` in the browser) still loads, and read as
     * "no column flagged missing".
     */
    coreColumns?: Record<IoohGroup, boolean>;
    /**
     * The base of each extra column, index for index with `extraColHeaders`, or null where the source
     * gives none (M4S-5626, BT-2055: a market is ranked only on bases it has). Optional for the same
     * reason as `coreColumns`.
     */
    extraColBases?: (number | null)[];
    /**
     * The survey's piped values by question ("BRANDCV": "Logitech G"), from a `...CV` question with one
     * answer (M4S-5673, BT-2064): a footnote prints the question as respondents saw it. Optional for the
     * same reason as `coreColumns`.
     */
    pipes?: Record<string, string>;
    /**
     * M4S-5777 X43: "Filtered to: … (n=…)" when the Studio crosstab the deck was built from was
     * filtered (`ComputedTable.filterNote`). Every data slide's footnote ends with it
     * (iooh-footnote.ts). Absent on an upload and on an unfiltered crosstab.
     */
    filterNote?: string;
    /**
     * M4S-5777 (X91): how the figures were weighted ("Weighted: Unexposed weighted to match Exposed on
     * OOHPEXP"), when the crosstab the deck was built from was weighted. Every data slide's footnote
     * says so. Absent for an unweighted crosstab and for an Excel upload, whose weighting is not read.
     */
    weighting?: string;
}

// ── Internal helpers ──────────────────────────────────────────────────────────

/**
 * Matches both section header formats:
 *  FORMAT A: "Table 5. OOHABA. Question text"  → capture group 1
 *  FORMAT B: "OOHABA — Question text"           → capture group 2
 *            (handles em-dash —, en-dash –, double-hyphen --, or single hyphen -)
 */
/**
 * M4S-5777 (X77, X81): statistic rows a Studio export can carry that are not answers: the standard
 * deviation, the standard error and Sigma (a column total, which can exceed 100%). A labelled row
 * with numbers is otherwise read as an answer, so these are skipped by their label. The lift (X72)
 * and index (X81) sub-rows need nothing here: they have no label and no '%', the count row's shape.
 */
const STAT_ROW_RE = /^(std\.? (deviation|error)|standard (deviation|error)|sigma\b)/i;

const SECTION_CODE_RE =
    /^Table\s+\d+\.\s*([A-Z][A-Z0-9_]*)\..*|^([A-Z][A-Z0-9_]*)\s*(?:—|–|--|-)\s/;

/**
 * The question text of a section header: what follows "Table 5. OOHABA." or "OOHABA —" (M4S-5673,
 * BT-2064). Empty when the header holds nothing after the code.
 */
export function sectionTitle(header: string): string {
    const formatA = /^Table\s+\d+\.\s*[A-Z][A-Z0-9_]*\.\s*/;
    const formatB = /^[A-Z][A-Z0-9_]*\s*(?:—|–|--|-)\s*/;
    return header.replace(formatA.test(header) ? formatA : formatB, '').trim();
}

function cellStr(val: ExcelJS.CellValue, numFmt?: string): string {
    if (val == null) return '';
    if (typeof val === 'string') return val.trim();
    // A number reads as the text its format shows ("72% Da"), as exports wrote it before M4A2A-570.
    if (typeof val === 'number') return displayOfNumber(val, numFmt);
    if (typeof val === 'boolean') return String(val);
    if (typeof val === 'object') {
        if ('result' in val)
            return cellStr((val as ExcelJS.CellFormulaValue).result as ExcelJS.CellValue, numFmt);
        if ('richText' in val) {
            return (val as ExcelJS.CellRichTextValue).richText
                .map((r) => r.text)
                .join('')
                .trim();
        }
    }
    return '';
}

/**
 * Parse a percentage string to a number.
 * Handles both clean values ("4%") and values with embedded stat-sig letters
 * ("4% C", "17% BC") produced by the flat export format. A lowercase run after the
 * uppercase one ("72% Da": the secondary confidence level) leaves the number readable,
 * because parseFloat stops at the first letter.
 *
 * @internal exported for tests.
 */
export function parsePct(s: string): number | null {
    if (!s) return null;
    // Strip trailing sig letters, e.g. "4% C" → "4%", "17% BC" → "17%"
    const cleaned = s
        .replace(/\s+[A-Z*]{1,3}\s*$/, '')
        .replace(/%/g, '')
        .replace(/,/g, '')
        .trim();
    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
}

/**
 * A figure above 100 is no figure (`withSharesOnly`). A negative one stays: a pre-calculated NPS row
 * runs from -100 to 100.
 */
function share(v: number | null): number | null {
    return v !== null && v > 100 ? null : v;
}

/**
 * A row's figures as shares. A figure above 100 is not a share: it is a count left standing where the
 * file printed no percentage under it (an open end's net read 318, a count against a base of about 840),
 * so it is read as no figure and never printed (BT-2121). Applied once a section is complete, after any
 * percentage row has patched the count row above it.
 */
export function withSharesOnly(row: IoohDataRow): IoohDataRow {
    return {
        ...row,
        unexposed: share(row.unexposed),
        exposed: share(row.exposed),
        adRecognizer: share(row.adRecognizer),
        noAdRecall: share(row.noAdRecall),
        extraCols: row.extraCols.map(share),
    };
}

/**
 * Extract the sig letter(s) from a raw cell string, e.g. "4% C" → "C".
 * Returns empty string if none.
 *
 * Exports print the secondary confidence level's letters in lowercase after the uppercase ones
 * ("72% Da"). Only the uppercase run is the primary level's, which is what the Exposed and
 * Unexposed checks below read, so a trailing lowercase run is ignored.
 *
 * @internal exported for tests.
 */
export function extractSigLetter(raw: string): string {
    const m = /\s+([A-Z*]{1,3})[a-z]*\s*$/.exec(raw.trim());
    return m ? m[1] : '';
}

/**
 * Parse sample size from a header cell like "Unexposed\nn=228" or "n=81".
 */
function parseBaseFromHeader(cell: string): number {
    const m = /n\s*=\s*([\d,]+)/i.exec(cell);
    return m ? parseInt(m[1].replace(/,/g, ''), 10) || 0 : 0;
}

/**
 * Find the first column index (in `headers`) whose lowercased text contains
 * one of the `keywords`, optionally excluding headers that also contain any
 * of `exclude`. Used to locate Unexposed/Exposed/etc. columns.
 */
function findColIdx(headers: string[], keywords: string[], exclude?: string[]): number {
    const lower = headers.map((h) => h.toLowerCase());
    for (const kw of keywords) {
        const idx = lower.findIndex((h) => {
            if (!h.includes(kw)) return false;
            if (exclude?.some((ex) => h.includes(ex))) return false;
            return true;
        });
        if (idx >= 0) return idx;
    }
    return -1;
}

/**
 * A stat sig row (FORMAT A only) contains only single capital letters or empty
 * cells in all data columns.
 */
function isSigRow(dataCells: string[]): boolean {
    const nonEmpty = dataCells.filter((c) => c !== '');
    if (nonEmpty.length === 0) return false;
    return nonEmpty.every((c) => /^[A-Z*]{1,2}$/.test(c.trim()));
}

// ── Public parser ─────────────────────────────────────────────────────────────

export async function parseIoohCrosstab(source: File | ArrayBuffer): Promise<ParsedIoohCrosstab> {
    const workbook = new ExcelJS.Workbook();
    const buf = source instanceof File ? await source.arrayBuffer() : source;
    await workbook.xlsx.load(buf);

    // Try "Total" sheet first (legacy format). Fall back to the first sheet after the cover that
    // is not the net-and-mean sheet (flat format exports use a project-named sheet, and the
    // one-tab-per-question export its first question's tab), and finally to the first sheet.
    // Exports made before M4S-5592 put that net-and-mean sheet ("Summary Tables") second, so the
    // choice is by name and not by position.
    const sheet = workbook.worksheets[dataSheetIndex(workbook.worksheets.map((w) => w.name))];
    if (!sheet) throw new Error('No worksheet found in crosstab file');

    const rawRows: string[][] = [];
    sheet.eachRow((row) => {
        const cells: string[] = [];
        row.eachCell({ includeEmpty: true }, (cell) =>
            cells.push(cellStr(cell.value, cell.numFmt)),
        );
        while (cells.length && !cells[cells.length - 1]) cells.pop();
        rawRows.push(cells);
    });

    if (rawRows.length < 2) throw new Error('Crosstab file appears empty');

    // Row 2 (index 1) holds column headers.
    const headerRow = rawRows[1] ?? [];

    // Locate core group columns. expIdx must exclude "unexposed" to avoid
    // matching "Unexposed (n=228)" which also contains the word "exposed".
    const unexpIdx = findColIdx(headerRow, ['unexposed', 'control', 'unexp']);
    const expIdx = findColIdx(headerRow, ['exposed', 'test'], ['unexposed']);
    // 'ad rec' is also inside "No Ad Recall" and "Non-Ad Recall", so those never stand in for the
    // recognizer column when the file has no recognizer header of its own.
    const adRecIdx = findColIdx(
        headerRow,
        ['ad recognizer', 'recognizer', 'ad rec'],
        ['no ad', 'non-ad', 'non ad'],
    );
    const noRecIdx = findColIdx(headerRow, [
        'no ad recall',
        'no recall',
        'non-ad recall',
        'non ad recall',
    ]);

    const coreMax = Math.max(unexpIdx, expIdx, adRecIdx, noRecIdx);
    const extraColHeaders = coreMax >= 0 ? headerRow.slice(coreMax + 1) : [];

    // Pre-read bases from the header row (flat format embeds "n=228" there).
    // These become the default for every section in the file. An explicit
    // "Base" row inside a section will override these values if present.
    const headerBases = {
        unexposed: unexpIdx >= 0 ? parseBaseFromHeader(headerRow[unexpIdx] ?? '') : 0,
        exposed: expIdx >= 0 ? parseBaseFromHeader(headerRow[expIdx] ?? '') : 0,
        adRecognizer: adRecIdx >= 0 ? parseBaseFromHeader(headerRow[adRecIdx] ?? '') : 0,
        noAdRecall: noRecIdx >= 0 ? parseBaseFromHeader(headerRow[noRecIdx] ?? '') : 0,
    };

    // For flat-format sig detection: each column's assigned letter is its
    // 1-based position among data columns (Total=A, Unexposed=B, ...).
    // A cell with value "4% B" in the Exposed column means Exposed is sig
    // higher than group B (= Unexposed), i.e. exposedSigHigher=true.
    // We also check the neighbouring "Other" column for the complementary signal.
    // M4S-5777 X12: the one column-letter rule; a 1-based position is index − 1.
    const unexpLetter = unexpIdx >= 1 ? columnLetter(unexpIdx - 1) : '';
    const expLetter = expIdx >= 1 ? columnLetter(expIdx - 1) : '';
    const otherUnxLetter = unexpIdx >= 1 ? columnLetter(unexpIdx) : '';
    const otherExpLetter = expIdx >= 1 ? columnLetter(expIdx) : '';

    const getVal = (row: string[], idx: number): number | null =>
        idx >= 0 ? parsePct(row[idx] ?? '') : null;

    const sections: Record<string, IoohSection> = {};
    const customSectionCodes: string[] = [];

    let code: string | null = null;
    let title = '';
    let sourceQid = '';
    let rows: IoohDataRow[] = [];
    let bases = { ...headerBases };
    let pendingRow: IoohDataRow | null = null;
    // M4S-5777 (X87): whether this section's weighted "Total" base row was read.
    let sawTotalBase = false;

    const flushSection = () => {
        if (!code) return;
        sections[code] = {
            code,
            ...(title ? { title } : {}),
            ...(sourceQid ? { sourceQid } : {}),
            rows: rows.map(withSharesOnly),
            bases: { ...bases },
            extraColHeaders,
        };
        if (!ALL_KNOWN_CODES.has(code)) customSectionCodes.push(code);
    };

    for (let i = 2; i < rawRows.length; i++) {
        const row = rawRows[i];
        if (!row.length) continue;
        const first = row[0] ?? '';
        // M4S-5777 (X104): a table's footnote row is a note, never a section or an answer.
        if (isFootnoteText(first)) {
            pendingRow = null;
            continue;
        }

        // ── Section header detection ──────────────────────────────────────────
        const secMatch = SECTION_CODE_RE.exec(first);
        if (secMatch) {
            flushSection();
            // A Top 2 / Bottom 2 summary table (Studio's own, or a legacy SOTG file's) shares its
            // question's code, but it is a second table for it and not a section: it must not
            // replace the question's real section (the last table per code wins) or list the
            // question once more as an appendix slide. A ranked question's Rank 2… and Full Rank
            // tables (M4S-5777) are the same. Nothing is read until the next header.
            if (isExtraTableTitle(first)) {
                code = null;
                rows = [];
                pendingRow = null;
                continue;
            }
            // Group 1 = FORMAT A ("Table X. CODE."), group 2 = FORMAT B ("CODE —")
            let rawCode = (secMatch[1] ?? secMatch[2]).toUpperCase();
            // Normalise non-standard codes to the canonical known codes.
            sourceQid = CODE_ALIASES[rawCode] ? rawCode : '';
            rawCode = CODE_ALIASES[rawCode] ?? rawCode;
            code = rawCode;
            title = sectionTitle(first);
            rows = [];
            // Re-seed bases from header so sections without an explicit base
            // row still carry the correct n= values.
            bases = { ...headerBases };
            sawTotalBase = false;
            pendingRow = null;
            continue;
        }

        if (!code) continue;

        // ── Explicit base / sample size row (FORMAT A) ────────────────────────
        // Also matches bare "Total" — the first labeled row in each FORMAT A section
        // is the section base (respondent count), not a percentage row.
        // M4S-5777 (X87): a weighted workspace export puts its unweighted and effective bases right
        // under the weighted "Total". The deck reads the "Total", as it does the computed table's,
        // and an effective base is never an answer row.
        if (/^effective base\s*$/i.test(first)) {
            pendingRow = null;
            continue;
        }
        if (sawTotalBase && /^unweighted base\s*$/i.test(first)) {
            pendingRow = null;
            continue;
        }
        if (/^base|^unweighted base|^total\s+respondent|^total\s*$/i.test(first)) {
            if (/^total\s*$/i.test(first)) sawTotalBase = true;
            // M4S-5777 (X87): a weighted base is a fraction ("437.6"), written to the file as a
            // number; dropping every non-digit read it as 4376. Read it as a number, to the nearest
            // whole respondent, as the table shows it.
            const parseBase = (idx: number) =>
                idx >= 0 ? Math.round(Number((row[idx] ?? '').replace(/[^0-9.]/g, ''))) || 0 : 0;
            bases.unexposed = parseBase(unexpIdx) || bases.unexposed;
            bases.exposed = parseBase(expIdx) || bases.exposed;
            bases.adRecognizer = parseBase(adRecIdx) || bases.adRecognizer;
            bases.noAdRecall = parseBase(noRecIdx) || bases.noAdRecall;
            pendingRow = null;
            continue;
        }

        // ── Stat sig row — FORMAT A only (separate row of letters) ───────────
        const dataCells = row.slice(1).map((c) => c ?? '');
        if (pendingRow && isSigRow(dataCells)) {
            const expRaw = expIdx >= 0 ? (row[expIdx] ?? '').trim() : '';
            const unxRaw = unexpIdx >= 0 ? (row[unexpIdx] ?? '').trim() : '';
            if (expRaw === 'A' || expRaw.includes(unexpLetter))
                pendingRow.sig.exposedSigHigher = true;
            if (unxRaw === 'B' || unxRaw.includes(expLetter))
                pendingRow.sig.unexposedSigHigher = true;
            pendingRow = null;
            continue;
        }

        // ── FORMAT A percentage row — updates values on the preceding labeled row
        // In "Table X." format, each item row has two sub-rows:
        //   Row 1: label + raw counts (e.g. "I liked it very much | 63 | 38")
        //   Row 2: empty label + percentages (e.g. "          | 21% | 39%")
        // The parser previously read only the count row. This block detects the
        // trailing percentage row (empty label, at least one "%" in data columns)
        // and patches the already-stored pendingRow with the correct percentage values.
        if (!first && pendingRow) {
            const rawUnx = unexpIdx >= 0 ? (row[unexpIdx] ?? '') : '';
            const rawExp = expIdx >= 0 ? (row[expIdx] ?? '') : '';
            const rawAdRec = adRecIdx >= 0 ? (row[adRecIdx] ?? '') : '';
            const rawNoRec = noRecIdx >= 0 ? (row[noRecIdx] ?? '') : '';
            if ([rawUnx, rawExp, rawAdRec, rawNoRec].some((v) => v.includes('%'))) {
                if (rawUnx) pendingRow.unexposed = parsePct(rawUnx) ?? pendingRow.unexposed;
                if (rawExp) pendingRow.exposed = parsePct(rawExp) ?? pendingRow.exposed;
                if (rawAdRec)
                    pendingRow.adRecognizer = parsePct(rawAdRec) ?? pendingRow.adRecognizer;
                if (rawNoRec) pendingRow.noAdRecall = parsePct(rawNoRec) ?? pendingRow.noAdRecall;
                for (let j = 0; j < pendingRow.extraCols.length; j++) {
                    const raw = row[coreMax + 1 + j] ?? '';
                    if (raw) pendingRow.extraCols[j] = parsePct(raw) ?? pendingRow.extraCols[j];
                }
                continue; // pendingRow stays set so the subsequent sig row can still update it
            }
        }

        // M4S-5777: a statistic that is not an answer (STAT_ROW_RE).
        if (first && STAT_ROW_RE.test(first.trim())) {
            pendingRow = null;
            continue;
        }

        // ── Data row ──────────────────────────────────────────────────────────
        if (first && !SECTION_CODE_RE.test(first)) {
            const unexpVal = getVal(row, unexpIdx);
            const expVal = getVal(row, expIdx);
            const adRecVal = getVal(row, adRecIdx);
            const noRecVal = getVal(row, noRecIdx);

            // Skip rows with no numeric data (sub-headers, blank dividers).
            if (unexpVal === null && expVal === null && adRecVal === null && noRecVal === null) {
                pendingRow = null;
                continue;
            }

            // FORMAT B inline sig-letter detection.
            // A column's value is sig higher than the group whose letter appears
            // in the cell. We detect Exposed > Unexposed two ways:
            //   1. The Exposed cell itself contains the Unexposed letter.
            //   2. The Other-of-Unexposed cell contains the Unexposed letter
            //      (meaning the "everyone else" group > Unexposed → Exposed > Unexposed).
            const rawExp = expIdx >= 0 ? (row[expIdx] ?? '') : '';
            const rawUnx = unexpIdx >= 0 ? (row[unexpIdx] ?? '') : '';
            const rawOtherUnx =
                unexpIdx >= 0 && unexpIdx + 1 < row.length ? (row[unexpIdx + 1] ?? '') : '';
            const rawOtherExp =
                expIdx >= 0 && expIdx + 1 < row.length ? (row[expIdx + 1] ?? '') : '';

            const sigExp = extractSigLetter(rawExp);
            const sigUnx = extractSigLetter(rawUnx);
            const sigOtherUnx = extractSigLetter(rawOtherUnx);
            const sigOtherExp = extractSigLetter(rawOtherExp);

            const exposedSigHigher =
                (unexpLetter && sigExp.includes(unexpLetter)) ||
                (unexpLetter && sigOtherUnx.includes(unexpLetter));
            const unexposedSigHigher =
                (expLetter && sigUnx.includes(expLetter)) ||
                (expLetter && sigOtherExp.includes(expLetter));

            const dataRow: IoohDataRow = {
                label: first,
                isNet: /\bnet\b|\btotal\b|\bcategory\b/i.test(first),
                unexposed: unexpVal,
                exposed: expVal,
                adRecognizer: adRecVal,
                noAdRecall: noRecVal,
                extraCols: extraColHeaders.map((_, j) => parsePct(row[coreMax + 1 + j] ?? '')),
                sig: {
                    exposedSigHigher: Boolean(exposedSigHigher),
                    unexposedSigHigher: Boolean(unexposedSigHigher),
                },
            };
            rows.push(dataRow);
            // Keep pendingRow for FORMAT A sig rows on the next iteration.
            pendingRow = dataRow;
        }
    }
    flushSection();

    // Log parsed sections to console so callers can verify what was detected.
    console.log('[iooh-parser] sections found:', Object.keys(sections));
    if (!sections['OOHMM']) {
        console.warn(
            '[iooh-parser] OOHMM section not found — slide 11 will show no data. Check that the Excel has an OOHMM section header (e.g. "OOHMM — Main Message" or "OOHMM - Main Message").',
        );
    }

    return {
        sections,
        customSectionCodes,
        extraColHeaders,
        extraColBases: extraColHeaders.map((h) => splitColumnHeader(h).base),
        coreColumns: {
            unexposed: unexpIdx >= 0,
            exposed: expIdx >= 0,
            adRecognizer: adRecIdx >= 0,
            noAdRecall: noRecIdx >= 0,
        },
    };
}
